import { useState } from 'react';
import { db, storage } from '../firebase/config';
import { collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import './CollectionProduct.css';

export default function AddProduct({ collectionId }) {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    images: [], // Adjusted for multiple images
    featured: false
  });

  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const handleImageChange = (e) => {
    // Store all selected images
    if (e.target.files) {
      setFormData({
        ...formData,
        images: [...e.target.files] // Store multiple files
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Upload each image and get the download URL
      const imageUrls = await Promise.all(
        formData.images.map(async (image) => {
          return new Promise((resolve, reject) => {
            const imageRef = ref(storage, `products/${image.name}`);
            const uploadTask = uploadBytesResumable(imageRef, image);

            // Listen for state changes, errors, and completion of the upload
            uploadTask.on(
              'state_changed',
              (snapshot) => {
                // Calculate and track total upload progress
                const progress = 
                  Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                setUploadProgress(progress);
              },
              (error) => {
                // Handle unsuccessful uploads
                console.error('Upload error:', error);
                reject(error);
              },
              async () => {
                // Handle successful upload
                try {
                  const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                  resolve(downloadURL);
                } catch (error) {
                  reject(error);
                }
              }
            );
          });
        })
      );

      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        images: imageUrls, // Save array of image URLs
        featured: formData.featured,
        collectionId,
        nameLowerCase: formData.name.toLowerCase()
      };

      // Save product data with image URLs array
      await addDoc(collection(db, 'products'), productData);
      
      // Reset upload state and navigate
      setIsUploading(false);
      navigate(`/collections/${collectionId}`);
    } catch (error) {
      setError('Error adding product: ' + error.message);
      setIsUploading(false);
    }
  };

  if (isUploading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner-overlay">
        <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <h2>Add New Product</h2>
      {error && <p className="error-message">{error}</p>}
      {isUploading && (
        <div className="upload-progress">
          <p>Uploading: {uploadProgress}%</p>
          <div 
            className="progress-bar" 
            style={{
              width: `${uploadProgress}%`, 
              height: '5px', 
              backgroundColor: 'green'
            }} 
          />
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Product Name</label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            disabled={isUploading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
            disabled={isUploading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="price">Price</label>
          <input
            type="number"
            id="price"
            step="0.01"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
            required
            disabled={isUploading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="images">Product Images</label>
          <input
            type="file"
            id="images"
            accept="image/*"
            multiple // Allow multiple image selection
            onChange={handleImageChange}
            required
            disabled={isUploading}
          />
        </div>

        <div className="form-group">
          <input
            type="checkbox"
            id="featured"
            checked={formData.featured}
            onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
            disabled={isUploading}
          />
          <label htmlFor="featured">Mark as Featured</label>
        </div>

        <button type="submit" disabled={isUploading}>
          Add Product
        </button>
      </form>
    </div>
  );
}